/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { type RouteRecordRaw } from 'vue-router';

import { Roles } from '@/components/Agency/UserFormData/types';
import { PageSubType } from '@/services/tracking/GtmTrackingService';
import { UserGroup } from '@/types';

import { hasAccessToProspectionMap } from '@/helpers/hasAccessToProspectionMap';
import { viewNamePrefix } from '@/router/routerConstants';
import store from '@/store';
import {
  packageOverviewGuard,
  referenceListingsGuard,
  vPlusRouteGuard,
} from '../routerGuardHelpers';
import routes from './routeNames';

const _default: RouteRecordRaw[] = [
  {
    path: '',
    name: 'business-listings-manage',
    component: () => import('@/views/ManageAdsPage/ManageAdsPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Listings Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.MY_ADS,
    },
  },
  {
    path: 'listings/new',
    name: 'business-listings-new',
    component: () => import('@/views/NewPage/NewPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} New Listing Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.INPUT_START,
    },
  },
  {
    path: 'listings/crm-upload-report',
    name: routes.crmUploadReport,
    component: () => import('@/views/CrmUploadReportPage/CrmUploadReportPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} CRM Upload Report Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.CRM_UPLOAD_REPORT,
    },
  },
  {
    path: 'listings/:id/details',
    name: 'business-listing-details',
    component: () => import('@/views/DetailsPage/DetailsPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Listing Details Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.INPUT_DETAILS,
    },
  },
  {
    path: 'listings/:id/details/promotion',
    name: 'business-listing-details-promotion',
    component: () => import('@/views/ListingPromotionPage/ListingPromotionPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Listing Promotion Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.INPUT_PROMOTION,
    },
  },
  {
    path: 'listings/:id/insights',
    name: 'business-listing-insights',
    component: () => import('@/views/ListingInsightsPage/ListingInsightsPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Listing Insights Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
    },
  },
  {
    path: 'listing-insights',
    name: 'listing-insights',
    component: () => import('@/views/ListingInsightsHubPage/ListingInsightsHubPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Listing Insights Hub Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.LISTING_INSIGHTS,
    },
  },
  {
    path: 'whats-new',
    name: routes.whatsNew,
    component: () => import('@/views/WhatsNewPage/WhatsNewPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} What's New Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.WHATS_NEW,
    },
  },
  {
    path: 'listings/:id/leads',
    name: 'listing-leads',
    component: () => import('@/views/ListingLeadsPage/ListingLeadsPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Listing Leads Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
    },
  },
  {
    path: routes.ecosystem,
    name: routes.ecosystem,
    component: () => import('@/views/EcosystemPage/EcosystemPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Ecosystem Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.PARTNERS,
    },
  },
  {
    name: routes.prospectionMap,
    path: 'agency/prospection-map',
    component: () => import('@/views/Agency/ProspectionMap.vue'),
    beforeEnter: async (to, from, next) => {
      const activeProducts = store.getters.getActiveProducts;
      if (!hasAccessToProspectionMap(activeProducts)) {
        const locale = from.params.locale || to.params.locale;
        const path = `/${locale}/ubm/not-found`;
        return next({ path });
      }
      next();
    },
    meta: {
      viewName: `${viewNamePrefix} Prospection Map Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.PROSPECTION_MAP,
    },
  },
  {
    path: 'agency/cost-report',
    name: routes.costReport,
    component: () => import('@/views/Agency/CostReport.vue'),
    meta: {
      viewName: `${viewNamePrefix} Cost Report Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.COST_REPORT,
    },
  },
  {
    path: 'agency/package-overview',
    name: routes.packageOverview,
    component: () => import('@/views/PackageOverviewPage/PackageOverviewPage.vue'),
    beforeEnter: packageOverviewGuard,
    meta: {
      viewName: `${viewNamePrefix} Package Overview Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.PACKAGE_OVERVIEW,
    },
  },
  {
    path: 'agency/invoices',
    name: routes.invoices,
    component: () => import('@/views/Agency/AgencyInvoices.vue'),
    meta: {
      viewName: `${viewNamePrefix} Invoices Page`,
      authorizedRoles: [Roles.Admin],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.INVOICES,
    },
  },
  {
    name: routes.userManagement.userManagement,
    path: 'agency/users',
    component: () => import('@/views/Agency/UserManagement.vue'),
    meta: {
      viewName: `${viewNamePrefix} Agency User Management Page`,
      authorizedRoles: [Roles.Admin],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.USER_MANAGEMENT,
    },
  },
  {
    name: routes.agencyProfile,
    path: 'agency/profile',
    component: () => import('@/views/Agency/AgencyProfile.vue'),
    meta: {
      viewName: `${viewNamePrefix} Agency Profile Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.AGENCY_PROFILE,
    },
  },
  {
    name: routes.contactList,
    path: 'agency/contact',
    component: () => import('@/views/Agency/AgencyContactList.vue'),
    meta: {
      viewName: `${viewNamePrefix} Contact Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.AGENCY_PROFILE,
    },
  },
  {
    name: routes.referenceListings,
    path: 'agency/reference-listings',
    component: () => import('@/views/Agency/ReferenceListings.vue'),
    beforeEnter: referenceListingsGuard,
    meta: {
      viewName: `${viewNamePrefix} Reference Listings Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.REFERENCE_LISTINGS,
    },
  },
  {
    name: routes.visibilityPlus,
    path: 'agency/visibility-plus',
    component: () => import('@/views/Agency/VisibilityPlus.vue'),
    beforeEnter: vPlusRouteGuard,
    meta: {
      viewName: `${viewNamePrefix} Visibility Plus Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.VISIBILITY_PLUS,
    },
  },
  {
    name: routes.userManagement.userInvite,
    path: 'agency/users/invite',
    component: () => import('@/views/Agency/UserInvite.vue'),
    meta: {
      viewName: `${viewNamePrefix} Business Agency User Invite Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.USER_INVITE,
    },
  },
  {
    path: 'confirm-invitation/:invitationId',
    name: 'confirm-invitation',
    component: () => import('@/views/Agency/UserConfirmInvitation.vue'),
    meta: { hasBlankLayout: true, viewName: `${viewNamePrefix} Confirm Invitation Page` },
  },
  {
    path: 'seller-leads/active-districts',
    name: routes.sellerLeadsActiveDistricts,
    component: () => import('@/views/SellerLeads/ActiveDistricts/ActiveDistrictsPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Seller Leads Active Districts Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      gaName: PageSubType.SELLER_LEADS_MANAGE_DISTRICTS,
    },
  },
  {
    path: 'seller-leads/:leadId',
    name: 'seller-lead-details',
    component: () => import('@/views/SellerLeads/Details/DetailsPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Seller Lead Details Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      hasBlankLayout: true,
    },
  },
  {
    path: 'seller-leads',
    name: routes.sellerLeads,
    component: () => import('@/views/SellerLeads/LeadsManagement/LeadsManagementPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Seller Leads Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizeGroups: [UserGroup.AGENCY],
      gaName: PageSubType.SELLER_LEADS,
    },
  },
  {
    path: 'profile',
    name: routes.userProfile,
    component: () => import('@/views/UserProfile/UserProfile.vue'),
    meta: {
      viewName: `${viewNamePrefix} User Profile Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
    },
  },
  {
    path: 'deeplink',
    name: routes.deeplink,
    component: () => import('@/views/RedirectPage/RedirectPage.vue'),
    meta: {
      viewName: `${viewNamePrefix} Deeplink Handler Page`,
      authorizedRoles: [Roles.Admin, Roles.Editor],
      authorizedGroups: [UserGroup.AGENCY],
      hasBlankLayout: true,
    },
  },
];
export default _default;
